<template>
  <b-sidebar id="sidebar-apply-template" :visible="isSidebarActive" bg-variant="white" sidebar-class="sidebar-lg w-25"
    shadow backdrop no-header no-close-on-backdrop right @hidden="resetForm"
    @change="(val) => $emit('update:is-sidebar-active', val)">
    <template #default="{ hide }">
      <div class="d-flex justify-content-between align-items-center content-sidebar-hezader px-2 py-1 bg-dark">
        <h5 class="mb-0 text-capitalize">
          {{ $t("dmp.templateConfig.applyTemplate") }}
        </h5>

        <feather-icon class="ml-1 cursor-pointer" icon="XIcon" size="16" @click="hide" />
      </div>

      <validation-observer #default="{ handleSubmit }" ref="refFormObserver">
        <b-form class="p-2" @submit.prevent="handleSubmit(onSubmit)" @reset.prevent="resetForm">
          <div>
            <div class="d-flex align-items-start">
              <div class="flex-grow-1">
                <validation-provider #default="validationContext" name="template" rules="required">
                  <b-form-group class="text-capitalize" :label="$t('dmp.templateConfig.applyExisting')"
                    label-for="id_template">
                    <v-select v-model="formData.id_template" :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                      :options="mergedTemplateOptions" :reduce="(val) => val.value" label="label" input-id="id_template"
                      :state="getValidationState(validationContext)" @input="onTemplateChange" />
                    <b-form-invalid-feedback>
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>
              </div>

              <b-button v-if="formData.id_template" variant="danger" class="ml-1 mt-2" style="height: 38px"
                @click="handleDelete()">
                <b-icon-trash scale="1.0" />
              </b-button>
            </div>

            <b-form-textarea v-if="formData.id_template" v-model="selectedDescription" :disabled="true" rows="3"
              class="mt-1" />
          </div>

          <!-- Form Actions -->
          <div class="d-flex flex-column mt-2" style="gap: 0.5rem">


            <div class="d-flex" style="gap: 0.5rem">
              <b-button block class="m-0 text-capitalize" :disabled="!formData.id_template"
                v-ripple.400="'rgba(255, 255, 255, 0.15)'" variant="primary" type="submit">
                {{ $t("dmp.templateConfig.applyTemplate") }}
              </b-button>
              <b-button class="flex-fill text-capitalize" v-ripple.400="'rgba(186, 191, 199, 0.15)'" type="button"
                variant="outline-secondary" @click="hide">
                {{ $t("actions.cancel") }}
              </b-button>
            </div>
          </div>
        </b-form>
      </validation-observer>
    </template>
  </b-sidebar>
</template>

<script>
import {
  BSidebar,
  BForm,
  BFormGroup,
  BFormInvalidFeedback,
  BButton,
  BFormTextarea,
} from "bootstrap-vue";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import vSelect from "vue-select";
import { ref, computed, getCurrentInstance } from "@vue/composition-api";
import { required } from "@validations";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import formValidation from "@core/comp-functions/forms/form-validation";
import Ripple from "vue-ripple-directive";
import store from "@/store";
import Swal from "sweetalert2";

export default {
  components: {
    BSidebar,
    BForm,
    BFormGroup,
    BFormInvalidFeedback,
    BButton,
    vSelect,
    ValidationProvider,
    ValidationObserver,
    BFormTextarea,
  },
  directives: {
    Ripple,
  },
  model: {
    prop: "isSidebarActive",
    event: "update:is-sidebar-active",
  },
  props: {
    isSidebarActive: {
      type: Boolean,
      required: true,
    },
    panelData: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    templateOptions: {
      type: Array,
      required: true,
    },
    uuid: {
      type: String,
      required: false,
      default: null,
    },
  },
  data() {
    return {
      required,
    };
  },
  setup(props, { emit }) {
    const blankData = {
      id_template: null,
    };

    const { proxy } = getCurrentInstance();
    const toast = useToast();
    const formData = ref(JSON.parse(JSON.stringify(blankData)));

    const mergedTemplateOptions = computed(() => {
      return Array.isArray(props.templateOptions) ? [...props.templateOptions] : [];
    });

    const onSubmit = () => {

      const payload = {
        id_panel: props.panelData.id,
        id_template: formData.value.id_template,
      };

      const userId = store.state.user.user.id
      proxy.$root.$emit('dmp-template-started', {
        userId: userId,
        panelId: props.panelData.id,
        templateId: formData.value.id_template,
        templateName: props.panelData.name
      });

      store
        .dispatch("dmp_templates/setTemplate", payload)
        .then((response) => {
          if (response.success) {
            toast({
              component: ToastificationContent,
              props: {
                title: response.message,
                icon: "CheckIcon",
                variant: "success",
              },
            });

            emit("refetch-data");
            emit("update:is-sidebar-active", false);
          } else {
            toast({
              component: ToastificationContent,
              props: {
                title: response.message,
                icon: "AlertTriangleIcon",
                variant: "danger",
              },
            });
          }
        })
        .catch((response) => {
          toast({
            component: ToastificationContent,
            props: {
              title: response.response.data.message,
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
        });
    };

    const selectedDescription = ref(null);

    const onTemplateChange = (val) => {
      const found = mergedTemplateOptions.value.find((t) => t.value === val);
      selectedDescription.value = found?.description || null;
    };

    const resetData = () => {
      formData.value = JSON.parse(JSON.stringify(blankData));
      selectedDescription.value = null;
    };

    const handleDelete = () => {
      Swal.fire({
        title: proxy.$t("dmp.templateConfig.deleteTemplate"),
        text: proxy.$t("dmp.templateConfig.deleteTemplateWarning"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: proxy.$t("actions.delete"),
        cancelButtonText: proxy.$t("actions.cancel"),
        customClass: {
          confirmButton: "btn btn-danger",
          cancelButton: "btn btn-outline-secondary ml-1",
        },
        buttonsStyling: false,
      }).then((result) => {
        if (result.isConfirmed) {
          confirmDelete();
        }
      });
    };

    const confirmDelete = () => {
      store
        .dispatch("dmp_templates/delete", formData.value.id_template)
        .then((response) => {
          if (response.success) {
            toast({
              component: ToastificationContent,
              props: {
                title: response.message,
                icon: "CheckIcon",
                variant: "success",
              },
            });

            emit("refetch-data");
            emit("update:is-sidebar-active", false);
          } else {
            toast({
              component: ToastificationContent,
              props: {
                title: response.message,
                icon: "AlertTriangleIcon",
                variant: "danger",
              },
            });
          }
        })
        .catch((response) => {
          toast({
            component: ToastificationContent,
            props: {
              title: response.response.data.message,
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
        });
    };

    const { refFormObserver, getValidationState, resetForm } = formValidation(resetData);

    return {
      formData,
      mergedTemplateOptions,
      onSubmit,
      refFormObserver,
      getValidationState,
      resetForm,
      selectedDescription,
      onTemplateChange,
      handleDelete,
      confirmDelete,
    };
  },
};
</script>
